import React from 'react';
import { Snackbar, Alert, AlertColor, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

interface NotificationProps {
    open: boolean;
    message: string;
    severity: AlertColor;
    onClose: () => void;
    autoHideDuration?: number;
}

// Custom slide transition for better visual effect
const SlideTransition = (props: TransitionProps & { children: React.ReactElement }) => {
    return <Slide {...props} direction="down" />;
};

export const NotificationSnackbar: React.FC<NotificationProps> = ({
    open,
    message,
    severity,
    onClose,
    autoHideDuration = 6000,
}) => {
    // Get severity colors
    const getSeverityColors = (severity: AlertColor) => {
        switch (severity) {
            case 'success':
                return {
                    main: '#2e7d32',
                    light: '#e8f5e9',
                    text: '#1b5e20',
                    badge: '#2e7d32',
                };
            case 'error':
                return {
                    main: '#d32f2f',
                    light: '#ffebee',
                    text: '#b71c1c',
                    badge: '#d32f2f',
                };
            case 'warning':
                return {
                    main: '#ed6c02',
                    light: '#fff3e0',
                    text: '#e65100',
                    badge: '#ed6c02',
                };
            case 'info':
                return {
                    main: '#0288d1',
                    light: '#e1f5fe',
                    text: '#01579b',
                    badge: '#0288d1',
                };
            default:
                return {
                    main: '#666',
                    light: '#f5f5f5',
                    text: '#333',
                    badge: '#666',
                };
        }
    };

    const colors = getSeverityColors(severity);

    // Enhanced styling with clear visual hierarchy
    const getAlertStyles = () => {
        return {
            width: '100%',
            minWidth: 'min(400px, calc(100vw - 32px))', // #261: fit narrow screens
            maxWidth: '650px',
            backgroundColor: '#ffffff',
            color: colors.text,
            borderRadius: '16px',
            boxShadow: '0px 8px 24px 0px rgba(0, 0, 0, 0.15)',
            padding: '0',
            overflow: 'hidden',
            border: `2px solid ${colors.light}`,
            fontFamily: 'Kan-Regular, sans-serif',
            direction: 'rtl' as const,

            // Alert structure
            display: 'flex',
            flexDirection: 'row' as const,
            alignItems: 'stretch',

            // Icon container - colored stripe on the right
            '& .MuiAlert-icon': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                margin: '0',
                backgroundColor: colors.main,
                color: '#ffffff',
                fontSize: '28px',
                minWidth: '70px',
                borderLeft: 'none',
            },

            // Message container
            '& .MuiAlert-message': {
                flex: 1,
                padding: '20px 24px',
                display: 'flex',
                flexDirection: 'column' as const,
                gap: '8px',
                fontFamily: 'Kan-Regular, sans-serif',
            },

            // Close button
            '& .MuiAlert-action': {
                padding: '12px 16px',
                margin: '0',
                alignItems: 'flex-start',

                '& button': {
                    color: colors.text,
                    opacity: 0.6,
                    transition: 'opacity 0.2s',

                    '&:hover': {
                        opacity: 1,
                        backgroundColor: colors.light,
                    },
                },
            },
        };
    };

    // Get severity label in Hebrew
    const getSeverityLabel = (severity: AlertColor): string => {
        switch (severity) {
            case 'success':
                return 'פעולה בוצעה בהצלחה';
            case 'error':
                return 'שגיאה';
            case 'warning':
                return 'אזהרה';
            case 'info':
                return 'מידע שימושי';
            default:
                return '';
        }
    };

    // Get icon for severity
    const getSeverityIcon = (severity: AlertColor): string => {
        switch (severity) {
            case 'success':
                return '✓';
            case 'error':
                return '×';
            case 'warning':
                return '⚠';
            case 'info':
                return 'i';
            default:
                return '';
        }
    };

    return (
        <Snackbar
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            TransitionComponent={SlideTransition}
            sx={{
                marginTop: '24px',
                '& .MuiSnackbarContent-root': {
                    padding: 0,
                },
            }}
        >
            <Alert onClose={onClose} severity={severity} sx={getAlertStyles()} variant="standard" icon={false}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'stretch',
                        width: '100%',
                        minHeight: '80px',
                    }}
                >
                    {/* Icon circle container - on the right for RTL */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '12px',
                        }}
                    >
                        {/* Icon circle */}
                        <div
                            style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                backgroundColor: colors.main,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                fontSize: '24px',
                                fontWeight: 'bold',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            }}
                        >
                            {getSeverityIcon(severity)}
                        </div>
                    </div>

                    {/* Content area */}
                    <div
                        style={{
                            flex: 1,
                            padding: '16px 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            justifyContent: 'center',
                        }}
                    >
                        {/* Severity badge */}
                        <div
                            style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                backgroundColor: colors.light,
                                color: colors.badge,
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '600',
                                width: 'fit-content',
                                border: `1px solid ${colors.main}30`,
                            }}
                        >
                            {getSeverityLabel(severity)}
                        </div>

                        {/* Message */}
                        <div
                            style={{
                                fontSize: '15px',
                                lineHeight: '1.6',
                                color: colors.text,
                                fontWeight: '400',
                                wordBreak: 'break-word',
                            }}
                        >
                            {message}
                        </div>
                    </div>
                </div>
            </Alert>
        </Snackbar>
    );
};

export default NotificationSnackbar;
